import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCategories } from '../../Contexts/CategoryContext';
import { useCompany } from '../../Contexts/CompanyContext';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../Firebase/Config';

const ThreadsIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 448 512"
    width="14"
    height="14"
    fill="currentColor"
    style={{ verticalAlign: "middle" }}
  >
    <path d="M331.5 235.7c2.2 .9 4.2 1.9 6.3 2.8 29.2 14.1 50.6 35.2 61.8 61.4 15.7 36.5 17.2 95.8-30.3 143.2-36.2 36.2-80.3 52.5-142.6 53l-.3 0c-70.2-.5-124.1-24.1-160.4-70.2-32.3-41-48.9-98.1-49.5-169.6l0-.5C17 184.3 33.6 127.2 65.9 86.2 102.2 40.1 156.2 16.5 226.4 16l.3 0c70.3 .5 124.9 24 162.3 69.9 18.4 22.7 32 50 40.6 81.7l-40.4 10.8c-7.1-25.8-17.8-47.8-32.2-65.4-29.2-35.8-73-54.2-130.5-54.6-57 .5-100.1 18.8-128.2 54.4-26.2 33.3-39.8 81.5-40.3 143.2 .5 61.7 14.1 109.9 40.3 143.3 28 35.6 71.2 53.9 128.2 54.4 51.4-.4 85.4-12.6 113.7-40.9 32.3-32.2 31.7-71.8 21.4-95.9-6.1-14.2-17.1-26-31.9-34.9-3.7 26.9-11.8 48.3-24.7 64.8-17.1 21.8-41.4 33.6-72.7 35.3-23.6 1.3-46.3-4.4-63.9-16-20.8-13.8-33-34.8-34.3-59.3-2.5-48.3 35.7-83 95.2-86.4 21.1-1.2 40.9-.3 59.2 2.8-2.4-14.8-7.3-26.6-14.6-35.2-10-11.7-25.6-17.7-46.2-17.8l-.7 0c-16.6 0-39 4.6-53.3 26.3l-34.4-23.6c19.2-29.1 50.3-45.1 87.8-45.1l.8 0c62.6 .4 99.9 39.5 103.7 107.7l-.2 .2 .1 0zm-156 68.8c1.3 25.1 28.4 36.8 54.6 35.3 25.6-1.4 54.6-11.4 59.5-73.2-13.2-2.9-27.8-4.4-43.4-4.4-4.8 0-9.6 .1-14.4 .4-42.9 2.4-57.2 23.2-56.2 41.8l-.1 .1z" />
  </svg>
);

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const { activeParentCategories, loading: categoriesLoading } = useCategories();
  const { companyProfile, socialMedia, location } = useCompany();

  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'duplicate'>('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');

  // Display up to 4 active parent categories
  const displayCategories = activeParentCategories.slice(0, 6);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCategoryClick = (e: React.MouseEvent, categoryId: string) => {
    e.preventDefault();
    navigate(`/shop?category=${encodeURIComponent(categoryId)}`);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setNewsletterStatus('error');
      setNewsletterMessage('Please enter your email address.');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setNewsletterStatus('error');
      setNewsletterMessage('Please enter a valid email address.');
      return;
    }

    try {
      setNewsletterStatus('loading');

      // Guests can't read the newsletters collection (Firestore rules only allow
      // create), so duplicates can't be checked with a query. Instead, the email
      // itself is used as the doc ID: a repeat signup becomes an "update" of an
      // existing doc, which the rules reject for non-staff, and that permission
      // error is what tells us it's a duplicate.
      const docId = encodeURIComponent(trimmedEmail.toLowerCase());
      const newsletterDoc = doc(db, 'newsletters', docId);

      await setDoc(newsletterDoc, {
        email: trimmedEmail,
        subscribedAt: Timestamp.now(),
        isActive: true,
      });

      setNewsletterStatus('success');
      setNewsletterMessage('Thank you for subscribing!');
      setEmail('');
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        setNewsletterStatus('duplicate');
        setNewsletterMessage('This email is already subscribed.');
        return;
      }
      console.error('Error subscribing to newsletter:', error);
      setNewsletterStatus('error');
      setNewsletterMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <footer className="bg3 p-t-75 p-b-32">
      <div className="container">
        <div className="row">
          {/* Categories */}
          <div className="col-sm-6 col-lg-3 p-b-50">
            <h4 className="stext-301 cl0 p-b-30">
              Categories
            </h4>

            <ul>
              {categoriesLoading ? (
                <li className="p-b-10">
                  <span className="stext-107 cl7">Loading...</span>
                </li>
              ) : displayCategories.length > 0 ? (
                displayCategories.map((category: any) => (
                  <li key={category.id} className="p-b-10">
                    {/* Link keeps the React tree alive — no full page reload */}
                    <Link
                      to={`/shop?category=${encodeURIComponent(category.id)}`}
                      className="stext-107 cl7 hov-cl1 trans-04"
                    >
                      {category.name}
                    </Link>
                  </li>
                ))
              ) : (
                <li className="p-b-10">
                  <span className="stext-107 cl7">No categories available</span>
                </li>
              )}
            </ul>
          </div>

          {/* Help */}
          <div className="col-sm-6 col-lg-3 p-b-50">
            <h4 className="stext-301 cl0 p-b-30">
              Help
            </h4>

            <ul>
              <li className="p-b-10">
                <Link
                  to="/test-drive"
                  onClick={(e) => { e.preventDefault(); window.dispatchEvent(new Event('open-test-drive-drawer')); }}
                  className="stext-107 cl7 hov-cl1 trans-04"
                >
                  Book a Test Drive
                </Link>
              </li>

              <li className="p-b-10">
                <Link to="/sell-your-car" className="stext-107 cl7 hov-cl1 trans-04">
                  Sell Your Car
                </Link>
              </li>

              <li className="p-b-10">
                <Link to="/faqs" className="stext-107 cl7 hov-cl1 trans-04">
                  FAQs
                </Link>
              </li>

              <li className="p-b-10">
                <Link to="/terms-and-conditions" className="stext-107 cl7 hov-cl1 trans-04">
                  Terms & Conditions
                </Link>
              </li>

              <li className="p-b-10">
                <Link to="/privacy-policy" className="stext-107 cl7 hov-cl1 trans-04">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Get In Touch */}
          <div className="col-sm-6 col-lg-3 p-b-50">
            <h4 className="stext-301 cl0 p-b-30">
              GET IN TOUCH
            </h4>

            <p className="stext-107 cl7 size-201">
              {companyProfile ? (
                <>
                  Any questions? Let us know
                  {location?.address ? ` at ${location.address}` : ''}
                  {companyProfile.contactInfo.phone ? ` or call us on ${companyProfile.contactInfo.phone}` : ''}
                </>
              ) : (
                'Any questions? Feel free to get in touch with us.'
              )}
            </p>

            <div className="p-t-27">
              {/* Social links are external — <a href target="_blank"> is correct here */}
              {socialMedia?.facebook && (
                <a
                  href={socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fs-18 cl7 hov-cl1 trans-04 m-r-16"
                >
                  <i className="fa fa-facebook"></i>
                </a>
              )}

              {socialMedia?.instagram && (
                <a
                  href={socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fs-18 cl7 hov-cl1 trans-04 m-r-16"
                >
                  <i className="fa fa-instagram"></i>
                </a>
              )}

              {socialMedia?.threads && (
                <a
                  href={socialMedia.threads}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fs-18 cl7 hov-cl1 trans-04 m-r-16"
                >
                  <ThreadsIcon />
                </a>
              )}
            </div>
          </div>

          {/* Newsletter */}
          <div className="col-sm-6 col-lg-3 p-b-50">
            <h4 className="stext-301 cl0 p-b-30">
              Newsletter
            </h4>

            <form onSubmit={handleNewsletterSubmit} noValidate>
              <div className="wrap-input1 w-full p-b-4">
                <input
                  className="input1 bg-none plh1 stext-107 cl7"
                  type="email"
                  name="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (newsletterStatus !== 'idle') {
                      setNewsletterStatus('idle');
                      setNewsletterMessage('');
                    }
                  }}
                  disabled={newsletterStatus === 'loading' || newsletterStatus === 'success'}
                />
                <div className="focus-input1 trans-04"></div>
              </div>

              {newsletterMessage && (
                <p
                  className="stext-107 p-t-8"
                  style={{
                    color:
                      newsletterStatus === 'success'
                        ? '#4CAF50'
                        : newsletterStatus === 'duplicate'
                        ? '#FF9800'
                        : '#f44336',
                    fontSize: '12px',
                  }}
                >
                  {newsletterMessage}
                </p>
              )}

              <div className="p-t-18">
                <button
                  type="submit"
                  className="flex-c-m stext-101 cl0 size-103 bg1 bor1 hov-btn2 p-lr-15 trans-04"
                  disabled={newsletterStatus === 'loading' || newsletterStatus === 'success'}
                >
                  {newsletterStatus === 'loading' ? 'Subscribing...' : newsletterStatus === 'success' ? 'Subscribed!' : 'Subscribe'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="p-t-40">
          <p className="stext-107 cl6 txt-center">
            Copyright &copy;{new Date().getFullYear()} All rights reserved | Kryptonian Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;